// apps/web/src/pages/Admin/AdminCollections.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../../services/adminApi';
import AnimatedCoinManager from './AnimatedCoinManager';
import {
    CollectionAvailabilityFields,
    EMPTY_AVAILABILITY,
    availabilityFromCollection,
    availabilityToPayload,
    validateAvailability,
} from './CollectionAvailabilityFields';
import {
    CollectionCompletionFields,
    EMPTY_COMPLETION,
    completionFromCollection,
    completionToPayload,
    countEntries,
    validateCompletion,
} from './CollectionCompletionFields';
import { formatDateTime } from './Campaigns/campaignUtils';
import { formatSeasonRange, seasonCountdownLabel } from '@aluvo/constants/src/collectionAvailability';
import styles from './AdminCollections.module.css';

const REWARD_TIER_OPTIONS     = ['bronze', 'silver', 'gold', 'legendary'];
const COLLECTION_TYPE_OPTIONS = ['custom', 'franchise', 'director', 'genre'];
const KIND_OPTIONS             = ['movie', 'tv'];

const EMPTY_ITEM = { tmdb_id: '', kind: 'movie', season_number: '' };

const EMPTY_FORM = {
    name:             '',
    type:             'custom',
    reward_coin_name: '',
    reward_tier:      'legendary',
    active:           true,
    reward_image_url: '',
    items:            [{ ...EMPTY_ITEM }],
    availability:     EMPTY_AVAILABILITY, // default: always available
    completion:       EMPTY_COMPLETION,   // default: complete all titles
};

/** "Complete all · 4 titles" / "Any 5 of 8" (from the API's completion summary). */
function requirementSummary(col) {
    const total = col.totalEntries ?? col.items?.length ?? 0;
    if ((col.completionType ?? col.completion_type) === 'MINIMUM_COUNT') {
        return `Any ${col.requiredForCompletion ?? col.required_count} of ${total}`;
    }
    return `Complete all · ${total} title${total === 1 ? '' : 's'}`;
}

const AVAILABILITY_FILTERS = [
    { value: 'all',       label: 'All' },
    { value: 'seasonal',  label: 'Seasonal' },
    { value: 'permanent', label: 'Permanent' },
];

const STATUS_LABEL = { active: 'ACTIVE', upcoming: 'UPCOMING', ended: 'ENDED' };

// ── Availability cell / chip (status comes from the API, never recomputed) ──
function AvailabilitySummary({ col, compact = false }) {
    if (!col.isSeasonal) return <span className={styles.availabilityPermanent}>Permanent</span>;
    const status = col.availabilityStatus;
    const countdown = status === 'active' ? seasonCountdownLabel(col.availableUntil) : '';
    const endingSoon = status === 'active' && /today|tomorrow|^[2-7] days/.test(countdown);
    return (
        <div className={styles.availabilityCell}>
            <span className={styles.availabilitySeasonal}>Seasonal{col.seasonLabel && !compact ? ` · ${col.seasonLabel}` : ''}</span>
            <span className={styles.availabilityRange}>{formatSeasonRange(col.availableFrom, col.availableUntil)}</span>
            <span className={`${styles.availabilityStatus} ${styles[`availability_${status}`] || ''}`}>
                {STATUS_LABEL[status] || status}
            </span>
            {endingSoon && <span className={styles.availabilityEnding} title="Ending soon">⏳ {countdown}</span>}
        </div>
    );
}

const HISTORY_LABEL = {
    collection_seasonality_enabled:    'Seasonality enabled',
    collection_seasonality_disabled:   'Seasonality disabled',
    collection_available_from_changed: 'Start changed',
    collection_available_until_changed:'End changed',
    collection_season_label_changed:   'Label changed',
    collection_completion_type_changed:'Completion requirement changed',
    collection_required_count_changed: 'Titles required changed',
    collection_coin_granted:           'Coin granted by admin',
    collection_coin_granted_override:  '⚠️ Coin granted OUTSIDE earning window',
};

function formatHistoryValue(field, value) {
    if (value === null || value === undefined || value === '') return '—';
    if (field === 'available_from' || field === 'available_until') return formatDateTime(value);
    return String(value);
}

// ── Collection Completion Requirement (editable) ────────────────────────────
function CollectionCompletionSection({ detail, onSaved }) {
    const [form, setForm]       = useState(() => completionFromCollection(detail));
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState(null);
    const [success, setSuccess] = useState(null);
    const entryCount = countEntries(detail.items || []);

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        const invalid = validateCompletion(form, entryCount);
        if (invalid) { setError(invalid); return; }
        setSaving(true);
        try {
            const saved = await adminApi.updateCollection(detail.id, completionToPayload(form));
            setSuccess('Completion requirement saved. It applies to future rewards only — coins already earned are never affected.');
            onSaved?.({
                completion_type:       saved?.completion_type ?? completionToPayload(form).completion_type,
                required_count:        saved?.required_count ?? null,
                completionType:        saved?.completionType,
                requiredForCompletion: saved?.requiredForCompletion,
            });
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to save the completion requirement.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>
                Completion Requirement
                <span className={styles.itemCount}>{requirementSummary({ ...detail, totalEntries: entryCount })}</span>
            </h3>
            {error   && <div className={styles.errorBanner}>{error}</div>}
            {success && <div className={styles.successBanner}>{success}</div>}
            <form onSubmit={handleSubmit} className={styles.coinForm}>
                <CollectionCompletionFields value={form} onChange={setForm} entryCount={entryCount} disabled={saving} idPrefix="edit-completion" />
                <div className={styles.coinFormActions}>
                    <button type="submit" className={styles.addItemSubmitBtn} disabled={saving}>
                        {saving ? 'Saving…' : '💾 Save Requirement'}
                    </button>
                </div>
            </form>
        </div>
    );
}

// ── Collection Availability (editable) ──────────────────────────────────────
function CollectionAvailabilitySection({ detail, onSaved }) {
    const [form, setForm]       = useState(() => availabilityFromCollection(detail));
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState(null);
    const [success, setSuccess] = useState(null);

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        const invalid = validateAvailability(form);
        if (invalid) { setError(invalid); return; }
        setSaving(true);
        try {
            const saved = await adminApi.updateCollection(detail.id, availabilityToPayload(form));
            setSuccess('Availability saved — it applies immediately. Coins already earned are never affected.');
            onSaved?.({
                is_seasonal:        saved?.is_seasonal ?? form.mode === 'seasonal',
                available_from:     saved?.available_from ?? null,
                available_until:    saved?.available_until ?? null,
                season_label:       saved?.season_label ?? null,
                isSeasonal:         saved?.isSeasonal ?? form.mode === 'seasonal',
                availabilityStatus: saved?.availabilityStatus,
                availableFrom:      saved?.availableFrom ?? null,
                availableUntil:     saved?.availableUntil ?? null,
                seasonLabel:        saved?.seasonLabel ?? null,
            });
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to save availability.');
        } finally {
            setSaving(false);
        }
    }

    const history = detail.availabilityHistory || [];

    return (
        <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>
                Availability
                <span className={styles.itemCount}><AvailabilitySummary col={detail} compact /></span>
            </h3>

            {error   && <div className={styles.errorBanner}>{error}</div>}
            {success && <div className={styles.successBanner}>{success}</div>}

            <form onSubmit={handleSubmit} className={styles.coinForm}>
                <CollectionAvailabilityFields value={form} onChange={setForm} disabled={saving} idPrefix="edit-availability" />
                <div className={styles.coinFormActions}>
                    <button type="submit" className={styles.addItemSubmitBtn} disabled={saving}>
                        {saving ? 'Saving…' : '💾 Save Availability'}
                    </button>
                </div>
            </form>

            {history.length > 0 && (
                <details className={styles.availabilityHistory}>
                    <summary>Change history ({history.length})</summary>
                    <ul>
                        {history.map((h, i) => (
                            <li key={`${h.createdAt}-${i}`}>
                                <span className={styles.date}>{formatDateTime(h.createdAt)}</span>
                                {' '}{HISTORY_LABEL[h.action] || h.action}
                                {h.field && h.field !== 'is_seasonal' && (
                                    <>: {formatHistoryValue(h.field, h.oldValue)} → {formatHistoryValue(h.field, h.newValue)}</>
                                )}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}

const EMPTY_ADD_ITEM = { tmdb_id: '', kind: 'movie', season_number: '' };

const EMPTY_COIN_FORM = {
    reward_coin_name:   '',
    has_mature_variant: false,
    visual_config:      '',
};

// ── Drag-and-Drop Image Upload Zone ─────────────────────────────────────────
function ImageDropZone({ label, currentUrl, variant, collectionId, onUploaded, disabled }) {
    const [dragging, setDragging]   = useState(false);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview]     = useState(null);
    const inputRef                  = useRef(null);

    // Sync preview with saved URL
    useEffect(() => { setPreview(currentUrl || null); }, [currentUrl]);

    async function processFile(file) {
        if (!file || !file.type.startsWith('image/')) return;
        // Optimistic local preview
        const objectUrl = URL.createObjectURL(file);
        setPreview(objectUrl);
        setUploading(true);
        try {
            const result = await adminApi.uploadCollectionImage(collectionId, file, variant);
            onUploaded?.(result);
        } catch (err) {
            console.error('Upload failed:', err);
            setPreview(currentUrl || null); // roll back
        } finally {
            setUploading(false);
        }
    }

    function onDrop(e) {
        e.preventDefault();
        setDragging(false);
        if (disabled || uploading) return;
        const file = e.dataTransfer.files?.[0];
        processFile(file);
    }

    function onDragOver(e) { e.preventDefault(); if (!disabled) setDragging(true); }
    function onDragLeave()  { setDragging(false); }
    function onFileChange(e) { processFile(e.target.files?.[0]); e.target.value = ''; }
    function onClick()      { if (!disabled && !uploading) inputRef.current?.click(); }

    return (
        <div className={styles.dropZoneWrapper}>
            <span className={styles.labelSmall}>{label}</span>
            <div
                className={[
                    styles.dropZone,
                    dragging  ? styles.dropZoneDragging  : '',
                    uploading ? styles.dropZoneUploading : '',
                    preview   ? styles.dropZoneHasImage  : '',
                ].filter(Boolean).join(' ')}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={onClick}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && onClick()}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={onFileChange}
                />
                {preview ? (
                    <>
                        <img src={preview} alt={label} className={styles.dropZonePreview} onError={() => setPreview(null)} />
                        {uploading && <div className={styles.dropZoneOverlay}>⏳</div>}
                        {!uploading && <div className={styles.dropZoneOverlay} style={{ opacity: 0 }}>✏️ Replace</div>}
                    </>
                ) : (
                    <div className={styles.dropZonePlaceholder}>
                        {uploading ? <span>Uploading…</span> : (
                            <>
                                <span className={styles.dropZoneIcon}>🖼️</span>
                                <span>Drop image or click</span>
                            </>
                        )}
                    </div>
                )}
            </div>
            {preview && !uploading && (
                <span className={styles.dropZoneSaved}>✓ Saved to GCS</span>
            )}
        </div>
    );
}

// ── Coin Management Section ──────────────────────────────────────────────────
function CoinManagementSection({ detail, onUpdated }) {
    const [coinForm, setCoinForm]         = useState(EMPTY_COIN_FORM);
    const [savingCoin, setSavingCoin]     = useState(false);
    const [generating, setGenerating]     = useState(false);
    const [removingCoin, setRemovingCoin] = useState(false);
    const [coinError, setCoinError]       = useState(null);
    const [coinSuccess, setCoinSuccess]   = useState(null);
    const [zoomedCoin, setZoomedCoin]     = useState(null); // { url, label }

    useEffect(() => {
        if (!detail) return;
        setCoinForm({
            reward_coin_name:   detail.reward_coin_name  ?? '',
            has_mature_variant: detail.has_mature_variant ?? false,
            visual_config:      detail.visual_config
                ? JSON.stringify(detail.visual_config, null, 2)
                : '',
        });
    }, [detail]);

    function setC(key, value) { setCoinForm(prev => ({ ...prev, [key]: value })); }

    function handleUploaded(result) {
        setCoinSuccess(`${result.variant} image uploaded ✓`);
        onUpdated?.();
    }

    async function handleSaveCoin(e) {
        e.preventDefault();
        setCoinError(null);
        setCoinSuccess(null);
        setSavingCoin(true);
        try {
            let parsedVisualConfig = undefined;
            if (coinForm.visual_config.trim()) {
                try { parsedVisualConfig = JSON.parse(coinForm.visual_config); }
                catch { setCoinError('visual_config must be valid JSON.'); setSavingCoin(false); return; }
            } else {
                parsedVisualConfig = null;
            }
            await adminApi.updateCollectionCoin(detail.id, {
                reward_coin_name:   coinForm.reward_coin_name || undefined,
                has_mature_variant: coinForm.has_mature_variant,
                visual_config:      parsedVisualConfig,
            });
            setCoinSuccess('Coin settings saved!');
            onUpdated?.();
        } catch (err) {
            setCoinError(err.response?.data?.message || err.message || 'Failed to save coin settings.');
        } finally {
            setSavingCoin(false);
        }
    }

    async function handleGenerateExamples() {
        if (!window.confirm('Generate example coins? This will overwrite existing examples.')) return;
        setCoinError(null);
        setCoinSuccess(null);
        setGenerating(true);
        try {
            const data = await adminApi.generateCollectionExamples(detail.id);
            const teen  = data.collection?.example_teen_coin_url;
            const adult = data.collection?.example_adult_coin_url;
            setCoinSuccess(`Examples generated!${teen ? ' Teen ✓' : ''}${adult ? ' Adult ✓' : ''}`);
            onUpdated?.();
        } catch (err) {
            setCoinError(err.response?.data?.message || err.message || 'Failed to generate examples.');
        } finally {
            setGenerating(false);
        }
    }

    async function handleRemoveCoin(variant) {
        const label = variant === 'all' ? 'all coin images' : `the ${variant} variant`;
        if (!window.confirm(`Remove ${label} from this collection?`)) return;
        setCoinError(null);
        setCoinSuccess(null);
        setRemovingCoin(variant);
        try {
            await adminApi.removeCollectionCoin(detail.id, variant);
            setCoinSuccess(`Removed ${label}.`);
            onUpdated?.();
        } catch (err) {
            setCoinError(err.response?.data?.message || err.message || 'Failed to remove coin.');
        } finally {
            setRemovingCoin(false);
        }
    }

    const hasCoin     = !!(detail?.reward_image_url);
    const hasTeen     = !!(detail?.teen_variant_url);
    const hasAdult    = !!(detail?.adult_variant_url);

    return (
        <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>🪙 Coin Management</h3>

            {coinError   && <div className={styles.errorBanner}>{coinError}</div>}
            {coinSuccess && <div className={styles.successBanner}>{coinSuccess}</div>}

            {/* ── Coin Zoom Modal ── */}
            {zoomedCoin && (
                <div className={styles.coinZoomOverlay} onClick={() => setZoomedCoin(null)}>
                    <div className={styles.coinZoomContent} onClick={e => e.stopPropagation()}>
                        <img
                            src={zoomedCoin.url}
                            alt={zoomedCoin.label}
                            className={styles.coinZoomImg}
                            onError={e => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className={styles.coinZoomLabel}>{zoomedCoin.label}</span>
                        <button
                            className={styles.coinZoomCloseBtn}
                            onClick={() => setZoomedCoin(null)}
                        >
                            ✕ Close
                        </button>
                    </div>
                </div>
            )}

            {/* ── Media type: static (default) or animated Signature coin ── */}
            <AnimatedCoinManager detail={detail} onUpdated={onUpdated} />

            {/* ── Drop Zones ── */}
            <div className={styles.dropZoneRow}>
                <ImageDropZone
                    label="Standard / Teen Source"
                    variant="standard"
                    currentUrl={detail?.reward_image_url}
                    collectionId={detail?.id}
                    onUploaded={handleUploaded}
                />
                <ImageDropZone
                    label="Teen Variant (optional)"
                    variant="teen"
                    currentUrl={detail?.teen_variant_url}
                    collectionId={detail?.id}
                    onUploaded={handleUploaded}
                />
                <ImageDropZone
                    label="Adult Variant (optional)"
                    variant="adult"
                    currentUrl={detail?.adult_variant_url}
                    collectionId={detail?.id}
                    onUploaded={handleUploaded}
                />
            </div>

            {/* ── Example coin previews ── */}
            {(detail?.example_teen_coin_url || detail?.example_adult_coin_url) && (
                <div className={styles.exampleCoinsRow}>
                    <span className={styles.labelSmall} style={{ marginBottom: 4 }}>Generated Examples</span>
                    <div className={styles.coinPreviews}>
                        {detail.example_teen_coin_url && (
                            <div className={styles.coinPreviewItem}>
                                <img
                                    src={detail.example_teen_coin_url}
                                    alt="Ex. Teen"
                                    className={`${styles.coinPreviewThumb} ${styles.coinPreviewThumbClickable}`}
                                    onClick={() => setZoomedCoin({ url: detail.example_teen_coin_url, label: 'Teen Example' })}
                                    title="Click to enlarge"
                                    onError={e => { e.currentTarget.style.display='none'; }}
                                />
                                <span className={styles.coinPreviewItemLabel}>Teen Ex.</span>
                            </div>
                        )}
                        {detail.example_adult_coin_url && (
                            <div className={styles.coinPreviewItem}>
                                <img
                                    src={detail.example_adult_coin_url}
                                    alt="Ex. Adult"
                                    className={`${styles.coinPreviewThumb} ${styles.coinPreviewThumbClickable}`}
                                    onClick={() => setZoomedCoin({ url: detail.example_adult_coin_url, label: 'Adult Example' })}
                                    title="Click to enlarge"
                                    onError={e => { e.currentTarget.style.display='none'; }}
                                />
                                <span className={styles.coinPreviewItemLabel}>Adult Ex.</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Settings form ── */}
            <form onSubmit={handleSaveCoin} className={styles.coinForm}>
                <div className={styles.coinFormGrid}>
                    <div className={styles.itemField} style={{ flex: '1 1 100%' }}>
                        <label className={styles.labelSmall}>Coin Name</label>
                        <input
                            className={styles.inputSmall}
                            type="text"
                            placeholder="e.g. MCU Legendary Coin"
                            value={coinForm.reward_coin_name}
                            onChange={e => setC('reward_coin_name', e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.itemField} style={{ flex: '1 1 100%' }}>
                        <label className={styles.labelSmall}>Visual Config (JSON)</label>
                        <textarea
                            className={styles.inputSmall}
                            placeholder={'{\n  "rim": { "color": "#FFD700" },\n  ...\n}'}
                            value={coinForm.visual_config}
                            onChange={e => setC('visual_config', e.target.value)}
                            rows={5}
                            style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.78rem', resize: 'vertical' }}
                        />
                    </div>
                    <div className={styles.itemField} style={{ flex: '1 1 100%' }}>
                        <label className={styles.toggleLabel}>
                            <input
                                type="checkbox"
                                className={styles.checkbox}
                                checked={coinForm.has_mature_variant}
                                onChange={e => setC('has_mature_variant', e.target.checked)}
                            />
                            <span style={{ fontSize: '0.85rem' }}>Has mature (teen/adult) variants</span>
                        </label>
                    </div>
                </div>

                <div className={styles.coinFormActions}>
                    <button type="submit" className={styles.addItemSubmitBtn} disabled={savingCoin}>
                        {savingCoin ? 'Saving…' : '💾 Save Settings'}
                    </button>
                    <button
                        type="button"
                        className={styles.generateBtn}
                        disabled={generating || (!hasCoin && !hasTeen)}
                        onClick={handleGenerateExamples}
                        title={(!hasCoin && !hasTeen) ? 'Upload a Standard or Teen image first' : 'Generate example coins and upload to GCS'}
                    >
                        {generating ? '⏳ Generating…' : '⚙️ Generate Examples'}
                    </button>
                    {(hasCoin || hasTeen || hasAdult) && (
                        <button
                            type="button"
                            className={styles.coinRemoveAllBtn}
                            disabled={!!removingCoin}
                            onClick={() => handleRemoveCoin('all')}
                        >
                            🗑 Remove All
                        </button>
                    )}
                </div>
            </form>

            <p className={styles.coinHint}>
                Drag images onto each zone (or click to browse). They upload immediately to
                <code> collection-icons/{detail?.slug}/</code> on GCS. Then click <strong>Generate Examples</strong> to produce the final coins.
            </p>
        </div>
    );
}

// ── Coin Owners ──────────────────────────────────────────────────────────────
const OWNERS_PAGE_SIZE = 50;

function CollectionOwnersSection({ collectionId }) {
    const [owners, setOwners]   = useState([]);
    const [total, setTotal]     = useState(null);
    const [page, setPage]       = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    const fetchOwners = useCallback(async (pageToLoad) => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminApi.listCollectionOwners(collectionId, { page: pageToLoad, limit: OWNERS_PAGE_SIZE });
            setOwners(prev => (pageToLoad === 1 ? data.owners : [...prev, ...data.owners]));
            setTotal(data.pagination.total);
            setPage(pageToLoad);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load coin owners.');
        } finally {
            setLoading(false);
        }
    }, [collectionId]);

    useEffect(() => { fetchOwners(1); }, [fetchOwners]);

    return (
        <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>
                Coin Owners
                <span className={styles.itemCount}>{total ?? '…'}</span>
            </h3>
            {error && <div className={styles.errorBanner}>{error}</div>}
            {!loading && !error && owners.length === 0 ? (
                <div className={styles.detailEmpty}>No users have earned this coin yet.</div>
            ) : (
                <div className={styles.itemsList}>
                    {owners.map(owner => (
                        <div key={owner.user_badge_id} className={styles.detailItemRow}>
                            {owner.user.avatar_url ? (
                                <img
                                    src={owner.user.avatar_url}
                                    alt=""
                                    className={styles.ownerAvatar}
                                    onError={e => { e.currentTarget.style.display = 'none'; }}
                                />
                            ) : (
                                <div className={styles.ownerAvatarPlaceholder}>👤</div>
                            )}
                            <div className={styles.detailItemInfo}>
                                <div className={styles.detailItemTitle}>
                                    {owner.user.display_name || owner.user.username || owner.user.email}
                                </div>
                                <div className={styles.detailItemMeta}>
                                    {owner.user.username && <span className={styles.tmdbId}>@{owner.user.username}</span>}
                                    <span className={styles.tmdbId}>{owner.user.email}</span>
                                </div>
                            </div>
                            <div className={styles.ownerEarnedAt}>
                                {owner.earned_at ? new Date(owner.earned_at).toLocaleDateString() : '—'}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {loading && <div className={styles.detailEmpty}>Loading owners…</div>}
            {!loading && total != null && owners.length < total && (
                <button className={styles.loadMoreBtn} onClick={() => fetchOwners(page + 1)}>
                    Load more ({total - owners.length} remaining)
                </button>
            )}
        </div>
    );
}

// ── Collection Details (editable) ────────────────────────────────────────────
function CollectionDetailsSection({ detail, onSaved }) {
    const [form, setForm]       = useState({ name: detail.name ?? '', type: detail.type ?? 'custom' });
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        setForm({ name: detail.name ?? '', type: detail.type ?? 'custom' });
    }, [detail.id, detail.name, detail.type]);

    const trimmedName = form.name.trim();
    const dirty = trimmedName !== detail.name || form.type !== detail.type;

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        if (!trimmedName) { setError('Collection name is required.'); return; }
        setSaving(true);
        try {
            const updates = { name: trimmedName, type: form.type };
            await adminApi.updateCollection(detail.id, updates);
            setSuccess('Collection details saved!');
            onSaved?.(updates);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to save collection details.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>Collection Details</h3>

            {error   && <div className={styles.errorBanner}>{error}</div>}
            {success && <div className={styles.successBanner}>{success}</div>}

            <form onSubmit={handleSubmit} className={styles.coinForm}>
                <div className={styles.coinFormGrid}>
                    <div className={styles.itemField} style={{ flex: '2 1 240px' }}>
                        <label className={styles.labelSmall} htmlFor="collection-name">Collection Name</label>
                        <input
                            id="collection-name"
                            className={styles.inputSmall}
                            type="text"
                            value={form.name}
                            onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                            maxLength={255}
                            disabled={saving}
                        />
                    </div>
                    <div className={styles.itemField} style={{ flex: '1 1 140px' }}>
                        <label className={styles.labelSmall} htmlFor="collection-type">Type</label>
                        <select
                            id="collection-type"
                            className={styles.selectSmall}
                            value={form.type}
                            onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
                            disabled={saving}
                        >
                            {COLLECTION_TYPE_OPTIONS.map(t => (
                                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className={styles.coinFormActions}>
                    <button type="submit" className={styles.addItemSubmitBtn} disabled={saving || !dirty}>
                        {saving ? 'Saving…' : '💾 Save Details'}
                    </button>
                </div>
            </form>
        </div>
    );
}

// ── Collection Detail Panel ──────────────────────────────────────────────────
function CollectionDetailPanel({ collectionId, onClose, onCollectionUpdated }) {
    const [detail, setDetail]         = useState(null);
    const [loadingDetail, setLoading] = useState(true);
    const [addItem, setAddItem]       = useState({ ...EMPTY_ADD_ITEM });
    const [adding, setAdding]         = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [panelError, setPanelError] = useState(null);

    const fetchDetail = useCallback(async () => {
        try {
            setLoading(true);
            const data = await adminApi.getCollection(collectionId);
            setDetail(data.collection);
        } catch (err) {
            console.error('Failed to load collection detail:', err);
        } finally {
            setLoading(false);
        }
    }, [collectionId]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    async function handleAddItem(e) {
        e.preventDefault();
        if (!addItem.tmdb_id) return;
        setPanelError(null);
        setAdding(true);
        try {
            await adminApi.addCollectionItem(collectionId, {
                tmdb_id:       parseInt(addItem.tmdb_id, 10),
                kind:          addItem.kind,
                season_number: addItem.season_number !== '' ? parseInt(addItem.season_number, 10) : null,
            });
            setAddItem({ ...EMPTY_ADD_ITEM });
            await fetchDetail();
            onCollectionUpdated?.();
        } catch (err) {
            setPanelError(err.response?.data?.message || err.message || 'Failed to add item.');
        } finally {
            setAdding(false);
        }
    }

    // Close the modal with Escape.
    useEffect(() => {
        function onKeyDown(e) { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); }
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    function handleDetailsSaved(updates) {
        setDetail(prev => ({ ...prev, ...updates }));
        onCollectionUpdated?.();
    }

    // Reload so the change history (audit log) includes this edit.
    async function handleAvailabilitySaved(updates) {
        setDetail(prev => ({ ...prev, ...updates }));
        onCollectionUpdated?.();
        try {
            const data = await adminApi.getCollection(collectionId);
            if (data?.collection) setDetail(data.collection);
        } catch { /* keep the optimistic values */ }
    }

    async function handleRemoveItem(itemId) {
        if (!window.confirm('Remove this item from the collection?')) return;
        setPanelError(null);
        setRemovingId(itemId);
        try {
            await adminApi.removeCollectionItem(collectionId, itemId);
            setDetail(prev => ({
                ...prev,
                items: prev.items.filter(i => i.id !== itemId),
            }));
            onCollectionUpdated?.();
        } catch (err) {
            // e.g. 409: removing it would leave an "any N" requirement impossible.
            setPanelError(err.response?.data?.message || err.message || 'Failed to remove item.');
        } finally {
            setRemovingId(null);
        }
    }

    return (
        <div className={styles.detailOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div
                className={styles.detailPanel}
                role="dialog"
                aria-modal="true"
                aria-labelledby="collection-detail-title"
            >
                <div className={styles.detailHeader}>
                    {detail ? (
                        <div>
                            <h2 id="collection-detail-title" className={styles.detailTitle}>{detail.name}</h2>
                            <div className={styles.detailMeta}>
                                <span className={`${styles.tierBadge} ${styles[`tier_${detail.reward_tier}`]}`}>{detail.reward_tier}</span>
                                <span className={styles.typeCell}>{detail.type}</span>
                                <span className={detail.active ? styles.statusActive : styles.statusInactive}>
                                    {detail.active ? '● Active' : '○ Inactive'}
                                </span>
                                <AvailabilitySummary col={detail} compact />
                            </div>
                        </div>
                    ) : <h2 id="collection-detail-title" className={styles.detailTitle}>Loading…</h2>}
                    <button className={styles.detailCloseBtn} onClick={onClose} aria-label="Close">✕</button>
                </div>

                {loadingDetail ? (
                    <div className={styles.loading}>Loading items…</div>
                ) : (
                    <div className={styles.detailBody}>
                        {detail && <CollectionDetailsSection detail={detail} onSaved={handleDetailsSaved} />}
                        {detail && <CollectionAvailabilitySection key={detail.id} detail={detail} onSaved={handleAvailabilitySaved} />}
                        {detail && <CollectionCompletionSection key={`completion-${detail.id}`} detail={detail} onSaved={handleAvailabilitySaved} />}

                        {/* ── Current Items ── */}
                        <div className={styles.detailSection}>
                            <h3 className={styles.detailSectionTitle}>
                                Required Items
                                <span className={styles.itemCount}>{detail?.items?.length ?? 0}</span>
                            </h3>

                            {detail?.items?.length === 0 ? (
                                <div className={styles.detailEmpty}>No items yet. Add one below.</div>
                            ) : (
                                <div className={styles.itemsList}>
                                    {detail.items.map(item => (
                                        <div key={item.id} className={styles.detailItemRow}>
                                            {item.media?.poster_url ? (
                                                <img
                                                    src={item.media.poster_url}
                                                    alt=""
                                                    className={styles.detailItemPoster}
                                                    onError={e => { e.currentTarget.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <div className={styles.detailItemPosterPlaceholder}>
                                                    {item.kind === 'movie' ? '🎬' : '📺'}
                                                </div>
                                            )}
                                            <div className={styles.detailItemInfo}>
                                                <div className={styles.detailItemTitle}>
                                                    {item.media?.title ?? `TMDB #${item.tmdb_id}`}
                                                </div>
                                                <div className={styles.detailItemMeta}>
                                                    <span className={styles.kindPill}>
                                                        {item.kind === 'movie' ? '🎬 Movie' : '📺 TV'}
                                                    </span>
                                                    {item.season_number != null && (
                                                        <span className={styles.seasonPill}>Season {item.season_number}</span>
                                                    )}
                                                    <span className={styles.tmdbId}>TMDB {item.tmdb_id}</span>
                                                </div>
                                            </div>
                                            <button
                                                className={styles.removeItemBtn}
                                                disabled={removingId === item.id}
                                                onClick={() => handleRemoveItem(item.id)}
                                                title="Remove item"
                                            >
                                                {removingId === item.id ? '…' : '✕'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* ── Add Item ── */}
                        <div className={styles.detailSection}>
                            <h3 className={styles.detailSectionTitle}>Add Item</h3>
                            {panelError && <div className={styles.errorBanner}>{panelError}</div>}
                            <form className={styles.addItemForm} onSubmit={handleAddItem}>
                                <div className={styles.addItemRow}>
                                    <div className={styles.itemField}>
                                        <label className={styles.labelSmall}>TMDB ID *</label>
                                        <input
                                            className={styles.inputSmall}
                                            type="number"
                                            min="1"
                                            placeholder="e.g. 1726"
                                            value={addItem.tmdb_id}
                                            onChange={e => setAddItem(prev => ({ ...prev, tmdb_id: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className={styles.itemField}>
                                        <label className={styles.labelSmall}>Kind *</label>
                                        <select
                                            className={styles.selectSmall}
                                            value={addItem.kind}
                                            onChange={e => setAddItem(prev => ({ ...prev, kind: e.target.value, season_number: '' }))}
                                        >
                                            {KIND_OPTIONS.map(k => (
                                                <option key={k} value={k}>{k === 'movie' ? '🎬 Movie' : '📺 TV Series'}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {addItem.kind === 'tv' && (
                                        <div className={styles.itemField}>
                                            <label className={styles.labelSmall}>Season # (optional)</label>
                                            <input
                                                className={styles.inputSmall}
                                                type="number"
                                                min="1"
                                                placeholder="All seasons"
                                                value={addItem.season_number}
                                                onChange={e => setAddItem(prev => ({ ...prev, season_number: e.target.value }))}
                                            />
                                        </div>
                                    )}
                                    <button
                                        type="submit"
                                        className={styles.addItemSubmitBtn}
                                        disabled={adding || !addItem.tmdb_id}
                                    >
                                        {adding ? 'Adding…' : '+ Add'}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* ── Coin Management ── */}
                        <CoinManagementSection
                            detail={detail}
                            onUpdated={fetchDetail}
                        />

                        {/* ── Coin Owners ── */}
                        <CollectionOwnersSection collectionId={collectionId} />
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function AdminCollections() {
    const [collections, setCollections] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [showForm, setShowForm]       = useState(false);
    const [form, setForm]               = useState(EMPTY_FORM);
    const [submitting, setSubmitting]   = useState(false);
    const [toggling, setToggling]       = useState(null);
    const [error, setError]             = useState(null);
    const [successMsg, setSuccessMsg]   = useState(null);
    const [search, setSearch]           = useState('');
    const [pagination, setPagination]   = useState(null);
    const [page, setPage]               = useState(1);
    const [selectedCollectionId, setSelectedCollectionId] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'active' | 'inactive'
    const [availabilityFilter, setAvailabilityFilter] = useState('all'); // 'all' | 'seasonal' | 'permanent'

    const fileInputRef = useRef(null);

    const fetchCollections = useCallback(async () => {
        try {
            setLoading(true);
            const data = await adminApi.listCollections({
                page, limit: 20, search,
                ...(availabilityFilter !== 'all' ? { availability: availabilityFilter } : {}),
            });
            setCollections(data.collections);
            setPagination(data.pagination);
        } catch (err) {
            console.error('Failed to load collections:', err);
        } finally {
            setLoading(false);
        }
    }, [page, search, availabilityFilter]);

    useEffect(() => {
        fetchCollections();
    }, [fetchCollections]);

    /* ── form field helpers ── */
    function setField(key, value) {
        setForm(prev => ({ ...prev, [key]: value }));
    }

    function setItem(index, key, value) {
        setForm(prev => {
            const items = [...prev.items];
            items[index] = { ...items[index], [key]: value };
            if (key === 'kind' && value === 'movie') items[index].season_number = '';
            return { ...prev, items };
        });
    }

    function addItem() {
        setForm(prev => ({ ...prev, items: [...prev.items, { ...EMPTY_ITEM }] }));
    }

    function removeItem(index) {
        setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
    }

    function handleImageFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        const objectUrl = URL.createObjectURL(file);
        setField('_previewUrl', objectUrl);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        setSuccessMsg(null);
        const availabilityError = validateAvailability(form.availability)
            || validateCompletion(form.completion, countEntries(form.items));
        if (availabilityError) { setError(availabilityError); return; }
        setSubmitting(true);
        try {
            const payload = {
                ...availabilityToPayload(form.availability),
                ...completionToPayload(form.completion),
                name:             form.name,
                type:             form.type,
                reward_coin_name: form.reward_coin_name,
                reward_tier:      form.reward_tier,
                active:           form.active,
                reward_image_url: form.reward_image_url || null,
                items: form.items.map(item => ({
                    tmdb_id:       parseInt(item.tmdb_id, 10),
                    kind:          item.kind,
                    season_number: item.season_number !== '' ? parseInt(item.season_number, 10) : null,
                })),
            };
            await adminApi.createCollection(payload);
            setSuccessMsg(`Collection "${form.name}" created successfully!`);
            setForm(EMPTY_FORM);
            setShowForm(false);
            fetchCollections();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to create collection.');
        } finally {
            setSubmitting(false);
        }
    }

    async function handleToggleActive(col) {
        setToggling(col.id);
        try {
            await adminApi.toggleCollectionActive(col.id, !col.active);
            setCollections(prev =>
                prev.map(c => c.id === col.id ? { ...c, active: !c.active } : c)
            );
        } catch (err) {
            console.error('Failed to toggle collection:', err);
        } finally {
            setToggling(null);
        }
    }

    return (
        <div className={styles.page}>
            {/* ── Detail Panel ── */}
            {selectedCollectionId && (
                <CollectionDetailPanel
                    collectionId={selectedCollectionId}
                    onClose={() => setSelectedCollectionId(null)}
                    onCollectionUpdated={fetchCollections}
                />
            )}

            {/* ── Header ── */}
            <div className={styles.header}>
                <h1 className={styles.pageTitle}>🗂️ Collections</h1>
                <button
                    className={styles.createBtn}
                    onClick={() => { setShowForm(v => !v); setError(null); setSuccessMsg(null); }}
                >
                    {showForm ? '✕ Cancel' : '+ New Collection'}
                </button>
            </div>

            {successMsg && <div className={styles.successBanner}>{successMsg}</div>}

            {/* ── Create Form ── */}
            {showForm && (
                <form className={styles.form} onSubmit={handleSubmit}>
                    <h2 className={styles.formTitle}>Create Collection</h2>
                    {error && <div className={styles.errorBanner}>{error}</div>}

                    <div className={styles.formGrid}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Collection Name *</label>
                            <input
                                className={styles.input}
                                type="text"
                                placeholder="e.g. Marvel Cinematic Universe"
                                value={form.name}
                                onChange={e => setField('name', e.target.value)}
                                required
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Reward Coin Name *</label>
                            <input
                                className={styles.input}
                                type="text"
                                placeholder="e.g. MCU Legendary Coin"
                                value={form.reward_coin_name}
                                onChange={e => setField('reward_coin_name', e.target.value)}
                                required
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Type</label>
                            <select
                                className={styles.select}
                                value={form.type}
                                onChange={e => setField('type', e.target.value)}
                            >
                                {COLLECTION_TYPE_OPTIONS.map(t => (
                                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Reward Tier</label>
                            <select
                                className={styles.select}
                                value={form.reward_tier}
                                onChange={e => setField('reward_tier', e.target.value)}
                            >
                                {REWARD_TIER_OPTIONS.map(t => (
                                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Active</label>
                            <label className={styles.toggleLabel}>
                                <input
                                    type="checkbox"
                                    checked={form.active}
                                    onChange={e => setField('active', e.target.checked)}
                                    className={styles.checkbox}
                                />
                                <span>{form.active ? 'Active (visible to users)' : 'Inactive (hidden)'}</span>
                            </label>
                        </div>
                    </div>

                    {/* ── Coin Template Image ── */}
                    <div className={styles.coinTemplateSection}>
                        <h3 className={styles.itemsTitle}>Coin Template Image</h3>
                        <p className={styles.itemsHint}>
                            Provide the URL of the poster / artwork used to generate this collection's reward coin.
                        </p>
                        <div className={styles.coinTemplateRow}>
                            <div className={styles.fieldGroupFull}>
                                <label className={styles.label}>Image URL</label>
                                <input
                                    className={styles.input}
                                    type="url"
                                    placeholder="https://…"
                                    value={form.reward_image_url}
                                    onChange={e => setField('reward_image_url', e.target.value)}
                                />
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.label}>Preview from file</label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className={styles.fileInput}
                                    onChange={handleImageFile}
                                />
                                <button
                                    type="button"
                                    className={styles.browseBtn}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    📎 Browse…
                                </button>
                            </div>

                            {(form._previewUrl || form.reward_image_url) && (
                                <div className={styles.coinPreviewWrap}>
                                    <img
                                        src={form._previewUrl || form.reward_image_url}
                                        alt="Coin template preview"
                                        className={styles.coinPreviewImg}
                                        onError={e => { e.currentTarget.style.display = 'none'; }}
                                    />
                                    <span className={styles.coinPreviewLabel}>Preview</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Availability (default: always available) ── */}
                    <div className={styles.coinTemplateSection}>
                        <CollectionAvailabilityFields
                            value={form.availability}
                            onChange={value => setField('availability', value)}
                            disabled={submitting}
                            idPrefix="create-availability"
                        />
                    </div>

                    {/* ── Completion requirement (default: complete all titles) ── */}
                    <div className={styles.coinTemplateSection}>
                        <CollectionCompletionFields
                            value={form.completion}
                            onChange={value => setField('completion', value)}
                            entryCount={countEntries(form.items)}
                            disabled={submitting}
                            idPrefix="create-completion"
                        />
                    </div>

                    {/* ── Media Items ── */}
                    <div className={styles.itemsSection}>
                        <div className={styles.itemsHeader}>
                            <h3 className={styles.itemsTitle}>Required Media Items</h3>
                            <button type="button" className={styles.addItemBtn} onClick={addItem}>
                                + Add Item
                            </button>
                        </div>
                        <p className={styles.itemsHint}>
                            Add each TV series or movie users must verify to unlock the collection coin.
                        </p>

                        {form.items.map((item, idx) => (
                            <div key={idx} className={styles.itemRow}>
                                <span className={styles.itemIndex}>#{idx + 1}</span>

                                <div className={styles.itemField}>
                                    <label className={styles.labelSmall}>TMDB ID *</label>
                                    <input
                                        className={styles.inputSmall}
                                        type="number"
                                        min="1"
                                        placeholder="e.g. 1726"
                                        value={item.tmdb_id}
                                        onChange={e => setItem(idx, 'tmdb_id', e.target.value)}
                                        required
                                    />
                                </div>

                                <div className={styles.itemField}>
                                    <label className={styles.labelSmall}>Kind *</label>
                                    <select
                                        className={styles.selectSmall}
                                        value={item.kind}
                                        onChange={e => setItem(idx, 'kind', e.target.value)}
                                    >
                                        {KIND_OPTIONS.map(k => (
                                            <option key={k} value={k}>{k === 'movie' ? '🎬 Movie' : '📺 TV Series'}</option>
                                        ))}
                                    </select>
                                </div>

                                {item.kind === 'tv' && (
                                    <div className={styles.itemField}>
                                        <label className={styles.labelSmall}>Season # (optional)</label>
                                        <input
                                            className={styles.inputSmall}
                                            type="number"
                                            min="1"
                                            placeholder="All seasons"
                                            value={item.season_number}
                                            onChange={e => setItem(idx, 'season_number', e.target.value)}
                                        />
                                    </div>
                                )}

                                {form.items.length > 1 && (
                                    <button
                                        type="button"
                                        className={styles.removeItemBtn}
                                        onClick={() => removeItem(idx)}
                                        title="Remove item"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className={styles.formActions}>
                        <button type="submit" className={styles.submitBtn} disabled={submitting}>
                            {submitting ? 'Creating…' : '✓ Create Collection'}
                        </button>
                        <button
                            type="button"
                            className={styles.cancelBtn}
                            onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setError(null); }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {/* ── Search ── */}
            <div className={styles.searchRow}>
                <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search collections…"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                />
                <div className={styles.filterPills}>
                    {['all', 'active', 'inactive'].map(f => (
                        <button
                            key={f}
                            className={`${styles.filterPill} ${activeFilter === f ? styles.filterPillActive : ''}`}
                            onClick={() => setActiveFilter(f)}
                        >
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                    ))}
                </div>
                <div className={styles.filterPills} role="group" aria-label="Availability filter">
                    {AVAILABILITY_FILTERS.map(f => (
                        <button
                            key={f.value}
                            className={`${styles.filterPill} ${availabilityFilter === f.value ? styles.filterPillActive : ''}`}
                            aria-pressed={availabilityFilter === f.value}
                            onClick={() => { setAvailabilityFilter(f.value); setPage(1); }}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                {pagination && (
                    <span className={styles.totalCount}>{pagination.total} total</span>
                )}
            </div>

            {/* ── List ── */}
            {loading ? (
                <div className={styles.loading}>Loading collections…</div>
            ) : collections.length === 0 ? (
                <div className={styles.empty}>
                    <div className={styles.emptyIcon}>🗂️</div>
                    <div>No collections yet. Create one above.</div>
                </div>
            ) : (
                <>
                    <div className={styles.table}>
                        <table>
                            <thead>
                            <tr>
                                <th>Name</th>
                                <th>Reward Coin</th>
                                <th>Tier</th>
                                <th>Type</th>
                                <th>Owners</th>
                                <th>Availability</th>
                                <th>Requirement</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Active</th>
                            </tr>
                            </thead>
                            <tbody>
                            {collections
                                .filter(col => {
                                    if (activeFilter === 'active') return col.active;
                                    if (activeFilter === 'inactive') return !col.active;
                                    return true;
                                })
                                .map(col => (
                                    <tr
                                    key={col.id}
                                    className={styles.clickableRow}
                                    onClick={() => setSelectedCollectionId(col.id)}
                                >
                                    <td>
                                        <div className={styles.collectionName}>{col.name}</div>
                                        <div className={styles.collectionSlug}>/{col.slug}</div>
                                    </td>
                                    <td>
                                        <div className={styles.coinNameCell}>
                                            {/* Animated coins list with their static poster — never autoplay in the table */}
                                            {(col.reward_poster_url || col.reward_image_url) && (
                                                <img
                                                    src={col.reward_poster_url || col.reward_image_url}
                                                    alt=""
                                                    className={styles.coinThumb}
                                                    onError={e => { e.currentTarget.style.display = 'none'; }}
                                                />
                                            )}
                                            {col.reward_coin_name}
                                            {col.reward_media_type === 'ANIMATED' && (
                                                <span className={styles.animatedBadge} title="Animated Signature Coin">💎 Signature</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`${styles.tierBadge} ${styles[`tier_${col.reward_tier}`]}`}>
                                            {col.reward_tier}
                                        </span>
                                    </td>
                                    <td className={styles.typeCell}>{col.type}</td>
                                    <td className={styles.ownerCountCell} title="Users who own this collection coin">
                                        {col.owner_count ?? 0}
                                    </td>
                                    <td>
                                        <AvailabilitySummary col={col} />
                                    </td>
                                    <td className={styles.typeCell}>{requirementSummary(col)}</td>
                                    <td>
                                        <span className={col.active ? styles.statusActive : styles.statusInactive}>
                                            {col.active ? '● Active' : '○ Inactive'}
                                        </span>
                                    </td>
                                    <td className={styles.date}>
                                        {col.createdAt ? new Date(col.createdAt).toLocaleDateString() : '—'}
                                    </td>
                                    <td onClick={e => e.stopPropagation()}>
                                        <button
                                            className={col.active ? styles.toggleBtnOn : styles.toggleBtnOff}
                                            disabled={toggling === col.id}
                                            onClick={() => handleToggleActive(col)}
                                            title={col.active ? 'Deactivate collection' : 'Activate collection'}
                                        >
                                            {toggling === col.id ? '…' : col.active ? 'Deactivate' : 'Activate'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination && pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={page === 1}
                                onClick={() => setPage(p => p - 1)}
                                className={styles.pageBtn}
                            >
                                Previous
                            </button>
                            <span className={styles.pageInfo}>Page {page} of {pagination.totalPages}</span>
                            <button
                                disabled={page === pagination.totalPages}
                                onClick={() => setPage(p => p + 1)}
                                className={styles.pageBtn}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}